import { curveLinear } from "d3-shape";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { LineChart } from "../charts/LineChart";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_learning_fig_LIT_COHORT";
import { datasets } from "../data/data_learning_fig_LIT_COHORT_datasets";
import { countryIdIso, getCountryName, mkGemDataDecoder, useFigureControlItems, useMultiSelectState } from "../domain";
import { useMedia } from "../hooks";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { io, O, pipe } from "../prelude";
export * from "../data/data_learning_fig_LIT_COHORT";
const DEFAULT_COUNTRIES = [
    "BFA",
    "ETH",
    "NPL"
];
const Indicator = io.keyof({
    cohort: null,
    period: null
});
const indicatorLookup = {
    cohort: defineMessage({
        id: "fig.learning.LITCOHORT.cohort",
        defaultMessage: "Cohort literacy rate (%)"
    }),
    period: defineMessage({
        id: "fig.learning.LITCOHORT.period",
        defaultMessage: "Period literacy rate (%)"
    })
};
export const Data = mkGemDataDecoder([
    "iso3c",
    "value"
], {
    ind_id: Indicator
});
export const Chart = ({ data })=>{
    const intl = useIntl();
    const screenMDown = !useMedia(breakpoints.m);
    const env = useConfig();
    const [selectedCountries, actions] = useMultiSelectState("countries");
    const countryData = React.useMemo(()=>{
        return data.map((x)=>({
                ...x,
                entity_name: getCountryName(env.countries, x.iso3c),
                ind_name: intl.formatMessage(indicatorLookup[x.ind_id])
            }));
    }, [
        intl,
        data,
        env.countries
    ]);
    const countryControlItems = useFigureControlItems(countryData, (x)=>countryIdIso.unwrap(x.iso3c), (x)=>x.entity_name);
    React.useEffect(()=>{
        actions.setSelectionControl("countries", {
            type: "MultiSelect",
            label: intl.formatMessage(defineMessage({
                id: "Countries",
                defaultMessage: "Countries"
            })),
            selected: O.some(DEFAULT_COUNTRIES),
            maxItems: 3
        }, countryControlItems);
    }, [
        intl,
        actions,
        countryControlItems
    ]);
    const chartData = React.useMemo(()=>{
        return countryData.filter(({ iso3c })=>pipe(selectedCountries, O.fold(()=>false, (xs)=>xs.includes(countryIdIso.unwrap(iso3c)))));
    }, [
        countryData,
        selectedCountries
    ]);
    const columnCount = pipe(selectedCountries, O.fold(()=>1, (xs)=>xs.length));
    return <LineChart tLabel={(s)=>s} height={screenMDown || columnCount > 3 ? M.chartHeight.s : M.chartHeight.m} minInnerWidth={screenMDown ? 60 : undefined} endLabel={!screenMDown} x="year" timeParse="%Y" timeFormat="%Y" numberFormat=".0%" column="entity_name" columns={columnCount} columnSort="none" category="datum.ind_name" colorLegend values={chartData} yTicks={[
        0,
        0.2,
        0.4,
        0.6,
        0.8,
        1
    ]} curve={curveLinear}/>;
};
export default withFigureIO({
    url: new URL("../data/data_learning_fig_LIT_COHORT.json", import.meta.url).href,
    csv: new URL("../data/data_learning_fig_LIT_COHORT.zip", import.meta.url).href,
    xlsx: new URL("../data/data_learning_fig_LIT_COHORT.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
